import { tr } from '@/lib/i18n-core';
import { useQuery } from '@tanstack/react-query';
import type { MortalityReport as Report } from '@his/shared';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, StatTile } from '@/components/ui/layout';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui/primitives';

export function MortalityReport({ from, to }: { from: string; to: string }) {
  const { can } = useAuth();
  const allowed = can('mortality.read');

  const report = useQuery({
    queryKey: ['report', 'mortality', from, to],
    queryFn: () => get<Report>(`/mortality/report${qs({ from, to })}`),
    enabled: allowed,
  });

  if (!allowed) return <Alert>{tr('ipd', 'mortality_figures_are_not_part_of_your_role')}</Alert>;

  if (report.isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Spinner /> {tr('ipd', 'loading')}
      </div>
    );
  }
  if (report.error || !report.data) return <Alert variant="destructive">{tr('ipd', 'the_mortality_report_could_not_be_loaded')}</Alert>;

  const data = report.data;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatTile label={tr('ipd', 'deaths')} value={String(data.total)} sub={tr('ipd', 'wards_and_emergency_in_this_period')} tone={data.total > 0 ? 'warning' : 'default'} />
        <StatTile label={tr('ipd', 'admissions_closed')} value={String(data.admissionsClosed)} sub={tr('ipd', 'discharged_lama_referred_absconded_or_expired')} />
        <StatTile
          label={tr('ipd', 'ward_mortality')}
          value={data.ipdMortalityRatePct === null ? '—' : `${data.ipdMortalityRatePct}%`}
          sub={tr('ipd', 'ward_deaths_over_admissions_closed')}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('ipd', 'by_ward')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              rows={data.byWard}
              rowKey={(row) => row.wardId ?? 'emergency'}
              empty={tr('ipd', 'no_deaths_in_this_period')}
              dense
              columns={[
                { header: tr('ipd', 'ward'), cell: (row) => row.ward },
                { header: tr('ipd', 'deaths'), align: 'right', cell: (row) => row.deaths },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('ipd', 'by_cause')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              rows={data.byCause}
              rowKey={(row) => row.cause}
              empty={tr('ipd', 'no_deaths_in_this_period')}
              dense
              columns={[
                { header: tr('ipd', 'cause'), cell: (row) => <span className="capitalize">{row.cause}</span> },
                { header: tr('ipd', 'deaths'), align: 'right', cell: (row) => row.deaths },
              ]}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('ipd', 'each_death')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            rows={data.rows}
            rowKey={(row) => row.id}
            empty={tr('ipd', 'no_deaths_in_this_period')}
            dense
            columns={[
              { header: tr('ipd', 'when'), cell: (row) => dateTime(row.deceasedAt) },
              {
                header: tr('ipd', 'patient'),
                cell: (row) => (
                  <div>
                    <p className="font-medium">{row.patient}</p>
                    {row.mrn && <p className="text-xs text-muted-foreground">{row.mrn}</p>}
                  </div>
                ),
              },
              { header: tr('ipd', 'where'), cell: (row) => row.ward },
              { header: tr('ipd', 'cause'), cell: (row) => row.cause },
              {
                header: '',
                cell: (row) => (
                  <div className="flex gap-1">
                    <Badge variant="secondary">{humanize(row.source)}</Badge>
                    {row.isMlc && <Badge variant="destructive">{tr('ipd', 'mlc')}</Badge>}
                  </div>
                ),
              },
            ]}
          />
          {data.truncated && <p className="mt-2 text-xs text-muted-foreground">{tr('ipd', 'showing_the_latest_500_narrow_the_dates_to_see')}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
